/**
 * ColumnFilter - Filter dropdown with search for table columns
 */

import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Tooltip,
} from "@material-ui/core";
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@material-ui/icons";
import { ColumnFilterButton } from "../styled";

/**
 * Column filter dropdown component
 * @param {Object} props
 * @param {Array} props.options - Available filter options
 * @param {Array} props.selectedValues - Currently selected values
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.label - Filter label
 */
const ColumnFilter = ({ options, selectedValues, onChange, label }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  const handleToggle = (value) => {
    const currentIndex = selectedValues.indexOf(value);
    const newSelected = [...selectedValues];
    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }
    onChange(newSelected);
  };

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    const lower = searchText.toLowerCase();
    return options.filter((opt) => String(opt).toLowerCase().includes(lower));
  }, [options, searchText]);

  const handleSelectAll = () => {
    const newSelected = [...new Set([...selectedValues, ...filteredOptions])];
    onChange(newSelected);
  };

  const handleClearAll = () => {
    if (searchText) {
      const filteredSet = new Set(filteredOptions);
      onChange(selectedValues.filter((v) => !filteredSet.has(v)));
    } else {
      onChange([]);
    }
  };

  const isFiltered = selectedValues.length !== options.length;

  return (
    <>
      <Tooltip title={`Filter ${label}`}>
        <ColumnFilterButton $active={isFiltered} onClick={handleClick}>
          <FilterIcon style={{ fontSize: 12 }} />
          <ArrowDropDownIcon style={{ fontSize: 14, marginLeft: -2 }} />
        </ColumnFilterButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock
        PaperProps={{
          style: {
            maxHeight: 350,
            minWidth: 200,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          px={1}
          py={0.5}
          borderBottom={1}
          borderColor="divider"
          style={{
            flexShrink: 0,
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
          }}
        >
          <TextField
            size="small"
            placeholder={`Search ${label}...`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            InputProps={{
              style: { fontSize: "0.75rem" },
            }}
            style={{ marginBottom: 4 }}
          />
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              color="primary"
              startIcon={<CheckIcon style={{ fontSize: 14 }} />}
              onClick={handleSelectAll}
              style={{ fontSize: "0.7rem", padding: "2px 6px", minWidth: 0 }}
            >
              All{searchText ? " visible" : ""}
            </Button>
            <Button
              size="small"
              color="secondary"
              startIcon={<ClearIcon style={{ fontSize: 14 }} />}
              onClick={handleClearAll}
              style={{ fontSize: "0.7rem", padding: "2px 6px", minWidth: 0 }}
            >
              Clear{searchText ? " visible" : ""}
            </Button>
          </Box>
        </Box>
        <Box style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {filteredOptions.length === 0 ? (
            <Box px={2} py={1}>
              <Typography variant="body2" color="textSecondary">
                No matches
              </Typography>
            </Box>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem key={option} dense onClick={() => handleToggle(option)}>
                <Checkbox
                  checked={selectedValues.indexOf(option) > -1}
                  size="small"
                  style={{ padding: 2, marginRight: 8 }}
                />
                <ListItemText
                  primary={option}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>
    </>
  );
};

export default ColumnFilter;
